import { restApi } from 'shared/api/rest-api';
import { getBrowserTimeZone } from 'shared/utils/browser-time-zone';

/**
 * Рендер шаблона media-manager на бэкенде: `POST /api/render-template` сам собирает
 * контекст из своей базы, зовёт сервис рендера и отдаёт готовый PNG.
 *
 * Запрос — **`multipart/form-data`**: фоны с 10.09.2026 загружаются файлами, а не
 * ссылками (`MediaManagerTemplateController::renderTemplate`, аннотация
 * `@OA\MediaType(mediaType="multipart/form-data")`). Скаляры едут отдельными полями
 * формы — именно так они перечислены в схеме аннотации.
 */
/**
 * `teamId` и `championshipId` необязательны, можно оба сразу: они только добавляют в
 * контекст данные команды и турнира. Связь игрока с ними бэкенд не проверяет.
 */
export interface RenderTemplateBirthdayArgs {
  /** Id пользователя (`User`), а не заявки. */
  playerId: number;
  teamId?: number;
  championshipId?: number;
}

export interface RenderTemplateArgs {
  templateId: number;
  /**
   * IANA-зона (`Europe/Moscow`), в которой печатать дату и время матча. Бэкенд требует
   * её с 11.09.2026 в любом режиме контекста и отвечает `400`, если зоны нет или её нет
   * в `DateTimeZone::listIdentifiers()`. Не задана — уходит зона браузера.
   */
  timezone?: string;
  matchId?: number;
  matchIds?: number[];
  championshipId?: number;
  stageId?: number;
  groupId?: number;
  /** Символическая сборная — режим `symbolic_team` у «Сборной тура» и «Сборной турнира». */
  symbolicTeamId?: number;
  /**
   * Карточка дня рождения одного игрока (режим `birthday`).
   *
   * Объектом, а не корневыми полями: корневой `championshipId` — режим статистики
   * игроков, и день рождения с ним ушёл бы не в тот контекст.
   */
  birthday?: RenderTemplateBirthdayArgs;
  templateSubtypes?: string[];
  /**
   * Загруженные картинки фонов — уезжают файлами в поле `backgrounds`, позиционно по
   * регионам layout-блоков.
   *
   * Байтами, а не ссылкой, потому что до внешних хостов из контейнера рендера не
   * достучаться: URL-фон отвечал `500 Render error`.
   */
  backgrounds?: Blob[];
  /** Старое поведение — фоны ссылками. Бэкенд переименовал прежнее поле в `backgroundUrls`. */
  backgroundUrls?: string[];
}

/**
 * Скаляр в форму — строкой: `FormData` всё равно приводит значение к строке, но явное
 * приведение оставляет в одном месте решение «что именно отправляем».
 */
function appendScalar(form: FormData, name: string, value: number | undefined): void {
  if (value === undefined) return;

  form.append(name, String(value));
}

/**
 * Массив — повторяющимся полем с `[]` в имени: только так php соберёт из плоской формы
 * массив (`$request->request->get('matchIds')`, `$request->files->get('backgrounds')`).
 * Пустой массив не отправляем вовсе — см. `buildRenderTemplateFormData`.
 */
function appendArray(form: FormData, name: string, values: readonly string[]): void {
  values.forEach((value) => form.append(`${name}[]`, value));
}

/**
 * Тело собирается строго под один режим контекста: на бэкенде ветки
 * `contextByMatchId` → `contextByMatchIds` → `contextByChampionshipId` → `contextByStageId`
 * идут подряд **без `else`**, поэтому лишний ключ молча меняет выбранный режим.
 * `symbolicTeamId` и `birthday` (`playerId`) — новые режимы, бэкенд по PRD выбирает их по
 * типу шаблона (`.claude/prd/prd-media-manager-new-template-types-backend.md`).
 * Ключи со значением `undefined` и пустые массивы не отправляем вовсе.
 */
export function buildRenderTemplateFormData(args: RenderTemplateArgs): FormData {
  const form = new FormData();

  appendScalar(form, 'templateId', args.templateId);
  // Обязательна при любом режиме, хотя время из неё сейчас читают только матчевые.
  form.append('timezone', args.timezone ?? getBrowserTimeZone());

  // Новые режимы — первыми: объект `birthday` и `symbolicTeamId` однозначно называют режим,
  // а после ветки `championshipId` корневой id рядом с `birthday` увёл бы запрос в статистику.
  if (args.symbolicTeamId !== undefined) {
    appendScalar(form, 'symbolicTeamId', args.symbolicTeamId);
  } else if (args.birthday) {
    // `championshipId` — только из объекта дня рождения, корневой не отправляется.
    appendScalar(form, 'playerId', args.birthday.playerId);
    appendScalar(form, 'teamId', args.birthday.teamId);
    appendScalar(form, 'championshipId', args.birthday.championshipId);
  } else if (args.matchId !== undefined) {
    appendScalar(form, 'matchId', args.matchId);
  } else if (args.matchIds?.length) {
    appendArray(form, 'matchIds', args.matchIds.map(String));
  } else if (args.championshipId !== undefined) {
    appendScalar(form, 'championshipId', args.championshipId);
  } else if (args.stageId !== undefined) {
    appendScalar(form, 'stageId', args.stageId);
    appendScalar(form, 'groupId', args.groupId);
  }

  if (args.templateSubtypes?.length) {
    appendArray(form, 'templateSubtypes', args.templateSubtypes);
  }

  if (args.backgroundUrls?.length) {
    appendArray(form, 'backgroundUrls', args.backgroundUrls);
  }

  // Имя файла обязательно: без него браузер шлёт часть как обычное поле, и php кладёт
  // её в `$request->request`, а не в `$request->files`.
  args.backgrounds?.forEach((background, index) => {
    form.append('backgrounds[]', background, `background-${index + 1}.${extensionOf(background)}`);
  });

  return form;
}

/** Расширение по mime — только для имени части; на разбор оно не влияет. */
function extensionOf(blob: Blob): string {
  return blob.type === 'image/png' ? 'png' : 'jpg';
}

const renderTemplateApi = restApi.injectEndpoints({
  endpoints: (build) => ({
    renderTemplate: build.mutation<Blob, RenderTemplateArgs>({
      query: (args) => ({
        url: '/render-template',
        method: 'POST',
        // `Content-Type` не задаём: axios сам ставит `multipart/form-data` вместе с
        // boundary, а свой заголовок затёр бы boundary и сломал разбор на бэкенде.
        data: buildRenderTemplateFormData(args),
        // ответ — image/png; при 4xx/5xx тело ошибки тоже придёт блобом,
        // поэтому потребитель различает ошибки по status
        responseType: 'blob',
      }),
    }),
  }),
  overrideExisting: false,
});

export const { useRenderTemplateMutation } = renderTemplateApi;

export default renderTemplateApi;
